import { Fragment, ReactElement, useCallback, useContext, useRef, useState } from 'react';

import { AddIcon } from '@breathelife/mui';
import {
  ApplicationType,
  CreateApplicationPayload,
  Language,
  LeadDetailTab,
  LineOfBusinessName,
  Permission,
} from '@breathelife/types';
import { ApplicationMode, ApplicationModes } from '@zinnia/form-engine-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userHasPermission } from '../../Helpers/user';
import { getMgaFromUserGroups } from '../../Helpers/userGroups';
import { useCarrierContext, useDispatch, useSelector } from '../../Hooks';
import { DetailViewType } from '../../Models/Layout';
import { Lead } from '../../Models/Lead';
import { generateApplicationUrl } from '../../Navigation/Urls';
import { LaunchApplicationModal } from '../../Pages/Home/Modals/LaunchApplicationModal';
import { LeadsPageDataContext } from '../../Pages/Leads/LeadsPageDataContextProvider';
import { useLaunchNewAssistedApplicationMutation } from '../../ReactQuery/AssistedApplication/assistedApplication.mutations';
import { useCreateLeadMutation, useUpdateLeadMutation } from '../../ReactQuery/Lead/lead.mutations';
import { useGetQuestionnaires } from '../../ReactQuery/Questionnaire/questionnaire.queries';
import { openRightPanel } from '../../ReduxStore/Layout/LayoutOperations';
import { ActionButton } from '../Button/ActionButton';
import { useFullPageLoaderContext } from '../LoadingView/FullPageLoader';
import { RestrictedToUserPermissions } from '../Restricted/RestrictedToUserPermissions';

type CreateButtonProps = {
  lead?: Lead;
};

export function CreateButton(props: CreateButtonProps): ReactElement {
  const { lead } = props;
  const { features, languageSettings } = useCarrierContext();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((store) => store.leadPlatform.authentication.user);
  const { openFullPageLoader, closeFullPageLoader } = useFullPageLoaderContext();
  const { isLoadingApplication } = useContext(LeadsPageDataContext);

  const isLeadCreationEnabled = features.leadCreation.enabled;
  const isPaperAppModeEnabled =
    features.questionnaireScreen?.enabled && userHasPermission(user?.permissions, [Permission.PaperApplicationCreate]);
  const isMultiBrandingEnabled = features.multiBranding.enabled;

  const onlyCreateLead = !lead && isLeadCreationEnabled;

  const [isCreateButtonModalOpen, setIsCreateButtonModalOpen] = useState<boolean>(false);
  const lineOfBusinessRef = useRef<LineOfBusinessName>(LineOfBusinessName.life);
  const applicationModeRef = useRef<ApplicationMode>(ApplicationModes.digital);
  const questionnaireIdRef = useRef<string>('');

  const {
    data: questionnaires = [],
    isLoading: getQuestionnairesIsLoading,
    isError: getQuestionnairesIsError,
  } = useGetQuestionnaires({ enabled: !onlyCreateLead });

  const updateLeadMutation = useUpdateLeadMutation({});

  const launchNewAssistedApplicationMutation = useLaunchNewAssistedApplicationMutation({
    onSuccess: (application) => {
      if (application && application.leadId) {
        updateLeadMutation.mutate({
          id: application.leadId,
        });
        navigate(generateApplicationUrl(application.id, application.leadId));
      }
    },
    onError: () => {
      closeFullPageLoader();
    },
  });

  const createLeadMutation = useCreateLeadMutation(
    {
      onSuccess: (createdLead) => {
        const payload: CreateApplicationPayload = {
          leadId: createdLead.id,
          type: ApplicationType.transactionFlow,
          mode: applicationModeRef.current,
          lineOfBusiness: lineOfBusinessRef.current,
          questionnaireId: questionnaireIdRef.current,
          userId: user?.id,
        };
        if (isMultiBrandingEnabled && user?.groups) {
          const mgaId = getMgaFromUserGroups(user?.groups)?.id;
          if (mgaId) payload.mgaId = mgaId;
        }
        launchNewAssistedApplicationMutation.mutate(payload);
      },
      onError: () => {
        closeFullPageLoader();
      },
    },
    true,
  );

  const onSubmit = useCallback(
    (
      mode: ApplicationMode,
      lineOfBusiness: LineOfBusinessName,
      questionnaireId: string,
      communicationLanguage?: Language,
    ) => {
      if (lead) {
        launchNewAssistedApplicationMutation.mutate(
          {
            leadId: lead.id,
            type: ApplicationType.transactionFlow,
            mode,
            lineOfBusiness,
            questionnaireId,
          },
          {
            onError: () => {
              closeFullPageLoader();
            },
          },
        );
      } else {
        lineOfBusinessRef.current = lineOfBusiness;
        applicationModeRef.current = mode;
        questionnaireIdRef.current = questionnaireId;
        createLeadMutation.mutate(
          {
            communicationLanguage,
          },
          {
            onError: () => {
              closeFullPageLoader();
            },
          },
        );
      }
    },
    [createLeadMutation, closeFullPageLoader, launchNewAssistedApplicationMutation, lead],
  );

  if (onlyCreateLead) {
    return (
      <RestrictedToUserPermissions requiredPermissions={[Permission.LeadCreate]}>
        <ActionButton
          data-testid='addNewLeadBtn-list-header'
          color='primary'
          variant='contained'
          onClick={() => dispatch(openRightPanel(DetailViewType.create, LeadDetailTab.personalInfo))}
          startIcon={<AddIcon htmlColor='white' />}
        >
          {t('leadsListTable.addNew')}
        </ActionButton>
      </RestrictedToUserPermissions>
    );
  }

  const languageChoices = !isLeadCreationEnabled ? languageSettings.enabledLanguages : [languageSettings.default];

  const hasMultipleQuestionnaires = questionnaires.length > 1;
  const hasMultipleLineOfBusinesses = questionnaires.some((q) => q.linesOfBusiness.length > 1);
  const hasMultipleLanguages = languageChoices.length > 1;
  const hasMultipleApplicationModes = isPaperAppModeEnabled;
  const needToOpenModal =
    hasMultipleApplicationModes || hasMultipleQuestionnaires || hasMultipleLineOfBusinesses || hasMultipleLanguages;

  const isCreateButtonDisabled =
    launchNewAssistedApplicationMutation.isLoading ||
    isLoadingApplication ||
    !user ||
    getQuestionnairesIsLoading ||
    getQuestionnairesIsError ||
    createLeadMutation.isLoading;

  return (
    <Fragment>
      {!getQuestionnairesIsLoading && questionnaires.length > 0 && (
        <ActionButton
          data-testid={lead ? 'create-application-and-launch-btn' : 'create-application-with-lead-and-launch-btn'}
          color='primary'
          variant='contained'
          startIcon={<AddIcon htmlColor='white' />}
          onClick={() => {
            if (!needToOpenModal) {
              openFullPageLoader();
              onSubmit(
                ApplicationModes.digital,
                questionnaires[0].linesOfBusiness[0].tag,
                questionnaires[0].id,
                languageSettings.enabledLanguages[0],
              );
              return;
            }
            setIsCreateButtonModalOpen(true);
          }}
          disabled={isCreateButtonDisabled}
        >
          {t('cta.launchApplication')}
        </ActionButton>
      )}
      <LaunchApplicationModal
        isOpen={isCreateButtonModalOpen}
        closeModal={() => setIsCreateButtonModalOpen(false)}
        onSubmitClick={(data) => {
          openFullPageLoader();
          onSubmit(data.mode, data.lineOfBusiness, data.questionnaireId, data.communicationLanguage);
        }}
        disableSubmitButton={isCreateButtonDisabled}
        questionnaires={questionnaires}
        applicationModes={
          isPaperAppModeEnabled ? [ApplicationModes.paper, ApplicationModes.digital] : [ApplicationModes.digital]
        }
        languageChoices={languageChoices}
      />
    </Fragment>
  );
}
